import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EllipsisVertical, Pencil, Trash, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { TableCell, TableRow } from '@/components/ui/table';

import DeleteActivityDialog from './DeleteActivityDialog';

import { useActivityObject } from '@/data/activity';

const ActivityRow = ({ activityId, onDelete }) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();
  const activity = useActivityObject(activityId);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: activityId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const handleRowClick = () => {
    navigate(`/activity/${activityId}`);
  };

  if (!activity) return null;

  return (
    <>
      <TableRow
        onClick={handleRowClick}
        className="cursor-pointer"
        ref={setNodeRef}
        style={style}
      >
        <TableCell className="w-[20px]">
          <div {...attributes} {...listeners} className="cursor-grab">
            <GripVertical className="w-4 h-4" />
          </div>
        </TableCell>
        <TableCell className="py-2">
          <div className="flex items-center gap-2">
            <Icon name={activity.icon} size={20} />
            {activity.title}
          </div>
        </TableCell>
        <TableCell className="py-2">{activity.feePercentage / 100}%</TableCell>
        <TableCell className="py-2">
          {!activity.isEnabled && <Badge variant="destructive">Disabled</Badge>}
        </TableCell>
        <TableCell className="text-right py-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="icon" size="icon" aria-label="Open menu">
                <EllipsisVertical size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
              <DropdownMenuLabel>{activity.title}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleRowClick}>
                <Pencil />
                Edit Activity
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash />
                Delete Activity
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
      {isDeleteDialogOpen && (
        <DeleteActivityDialog
          activity={activity}
          setOpen={setIsDeleteDialogOpen}
          onDelete={onDelete}
        />
      )}
    </>
  );
};

export default ActivityRow;
